import React, { useRef, useMemo } from 'react';
import { Marker } from 'react-leaflet';
import L from 'leaflet';

const DeviceMarker = React.memo(({ device, onClick, modelIcons = {}, isSelected = false }) => {
  const markerRef = useRef(null);

  // pozycja aktualizuje Marker sama; efekt z setLatLng nie jest konieczny
  const position = useMemo(() => [device.latitude, device.longitude], [
    device.latitude,
    device.longitude,
  ]);

  const iconData = useMemo(() => {
    const model = device.deviceModel || device.model;
    let imgSrc = 'car.png';
    let sosClass = '';
    let zIndex = 0;

    // --- NEW: Check Confirmation via attributes ---
    const attributes = device.attributes || {};
    const actionButton = attributes.action?.button; // Safety fallback
    const carStatus = attributes.CarStatus;

    // Confirmed if CarStatus says so, OR fallback to action.button
    const isRedFlagConfirmed = carStatus === 'REDFLAG_CONFIRM' || actionButton === 'CONFIRM';

    const warningIcon = modelIcons['Warning'] || 'car_warning.png';

    // Priority 1: Red Flag Confirmed
    if (isRedFlagConfirmed) {
      imgSrc = 'car_REDFLAG_CONFIRM.png';
      sosClass = ' red-flag-confirm';
      zIndex = 2000; // Top priority
    }
    // Priority 2: SOS Status — only for explicit SOS messages, NOT stop-timer
    // device.sos is also set by the stop-timer; use isSosActive to distinguish real SOS
    else if (carStatus === 'SOS' || attributes.sos === true || device.isSosActive === true) {
      imgSrc = 'car_SOS.png';
      sosClass = ' pulse-sos-signal';
      zIndex = 1800;
    }
    // Priority 3: Red Flag Sent or received via SMS
    else if (carStatus === 'REDFLAG_SEND' || carStatus === 'REDFLAG' || attributes.redflag === true) {
      imgSrc = 'car_REDFLAG.png';
      sosClass = ' red-flag-sent';
      zIndex = 1500;
    }
    // Priority 4: Explicit OK confirmation from driver via SMS
    else if (carStatus === 'OK') {
      imgSrc = 'car_OK.png';
      zIndex = 200;
    }
    // Priority 5: Off-track exit (car left the stage before the finish line)
    else if (device.exitedMidStage) {
      imgSrc = warningIcon;
      sosClass = ' pulse-warning';
      zIndex = 1100;
    }
    // Priority 6: Standard model-based fallback
    else if (!model || model === 'Zawodnik') {
      if (device.sos && (device.isSosActive || device.isStopped)) {
        imgSrc = warningIcon;
        sosClass = ' pulse-warning';
        zIndex = 1000;
      } else if (device.ignition) {
        imgSrc = 'car_OK.png';
        zIndex = 500;
      }
    } else if (modelIcons[model]) {
      imgSrc = modelIcons[model];
      if (device.sos && (device.isSosActive || device.isStopped)) {
        sosClass = ' pulse-warning';
      }
    }

    const name = device.deviceName ? device.deviceName.split('-')[0] : 'Unknown Device';
    const countdownHtml = Number.isFinite(device.countdownSec)
      ? `<div class="countdown-badge">${device.countdownSec}</div>`
      : Number.isFinite(device.offTrackCountdownSec)
        ? `<div class="countdown-badge countdown-badge--offtrack">${device.offTrackCountdownSec}</div>`
        : '';

    const isRedFlagIcon = imgSrc === 'car_REDFLAG.png' || imgSrc === 'car_REDFLAG_CONFIRM.png';
    const iconSize = isRedFlagIcon ? [40, 56] : [30, 42];
    const iconAnchor = isRedFlagIcon ? [20, 56] : [15, 42];

    const staleImgStyle = device.isStale ? 'width:100%;height:100%;opacity:0.4;filter:grayscale(60%);' : 'width:100%;height:100%;';
    const staleTextStyle = device.isStale ? 'font-size:10px;text-align:center;color:#000;font-weight:bold;' : 'font-size:10px;text-align:center;';

    return {
      icon: L.divIcon({
        className: 'custom-div-icon' + sosClass,
        html: `
        <div class="marker-content" data-deviceid="${device.deviceId || device.id}">
          <img src="/${imgSrc}" alt="Icon" style="${staleImgStyle}" />
                    ${countdownHtml}
          <div class="marker-text" style="${staleTextStyle}">${name}</div>
        </div>
      `,
        iconSize,
        iconAnchor,
      }),
      zIndex,
    };
  }, [
    device.deviceId,
    device.id,
    device.deviceModel,
    device.model,
    device.ignition,
    device.sos,
    device.isSosActive,
    device.isStopped,
    device.deviceName,
    device.countdownSec,
    device.attributes,
    device.exitedMidStage,
    device.offTrackCountdownSec,
    device.isStale,
    modelIcons,
    isSelected,
  ]);

  return (
    <Marker
      ref={markerRef}
      position={position}
      icon={iconData.icon}
      zIndexOffset={isSelected ? 10000 : iconData.zIndex}
      riseOnHover
      eventHandlers={{ click: (event) => onClick(device, event) }}
    />
  );
});

export default DeviceMarker;
